import { inject, Injectable } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ICustomCtaValue, IGlobalCtaValue } from '@campaign-form/campaign-form.interface';
import { EAnalyticsStatus } from '@enums/campaign-analytics.enum';
import { EDeliverableType } from '@enums/campaign.enum';
import { TranslateService } from '@ngx-translate/core';
import { intervalRangeValidator } from '@shared/validators/interval-range-validator';
import { numberValidator } from '@shared/validators/number-validator';
import isNil from 'lodash/isNil';
import { v4 as uuidv4 } from 'uuid';
import { IDeliverableConfig, TDeliverable } from '../deliverable-list.interface';
import { ETextDeliverableMatchMode } from '../shared/text-deliverable/text-deliverable.interface';
import { ChatbotMultiComponent } from './chatbot-multi.component';
import {
  IChatbotMultiDeliverable,
  IChatbotMultiDeliverableForm,
  IChatbotMultiKeywordForm,
} from './chatbot-multi.interface';

@Injectable({
  providedIn: 'root',
})
export class ChatbotMultiService {
  private readonly translate = inject(TranslateService);

  public getConfig(): IDeliverableConfig {
    return {
      component: ChatbotMultiComponent,
      createForm: (
        item?: TDeliverable,
        creatorId?: string,
        isViewMode?: boolean,
      ): FormGroup<IChatbotMultiDeliverableForm> | null => {
        if (item && item.type !== EDeliverableType.chatbotMulti) {
          return null;
        }

        const itemTyped = item as IChatbotMultiDeliverable | undefined;
        const intervalValidators = isViewMode ? [] : [numberValidator()];
        const keywordTextValidators = isViewMode ? [] : [Validators.required, Validators.maxLength(512)];

        const form = new FormGroup(
          {
            customCtaValues: new FormControl<ICustomCtaValue[]>(itemTyped?.customCtaValues ?? []),
            exclusiveTo: new FormControl<string | null>(itemTyped?.exclusiveTo || creatorId || null),
            globalCtaValues: new FormControl<IGlobalCtaValue[] | null>(itemTyped?.globalCtaValues ?? null),
            id: new FormControl<string | null>(itemTyped?.id ?? uuidv4()),
            isNew: new FormControl<boolean>(!itemTyped?.id),
            keywords: new FormArray<FormGroup<IChatbotMultiKeywordForm>>(
              (itemTyped?.keywords ?? [{ id: uuidv4(), text: '' }]).map(
                (keyword) =>
                  new FormGroup<IChatbotMultiKeywordForm>({
                    analyticsCurrentCount: new FormControl<number | null>(null),
                    customCtaValues: new FormControl<ICustomCtaValue[]>(keyword?.customCtaValues ?? [], {
                      nonNullable: true,
                    }),
                    exclusiveTo: new FormControl<string | null | undefined>(keyword.exclusiveTo || creatorId || null),
                    globalCtaValues: new FormControl<IGlobalCtaValue[] | null>(keyword.globalCtaValues ?? null),
                    id: new FormControl<string | null>(keyword.id ?? uuidv4()),
                    status: new FormControl<EAnalyticsStatus | null>(keyword.status ?? null),
                    text: new FormControl<string | null>(keyword.text, keywordTextValidators),
                  }),
              ),
            ),
            matchMode: new FormControl<ETextDeliverableMatchMode>(
              itemTyped?.matchMode ?? ETextDeliverableMatchMode.and,
              { nonNullable: true },
            ),
            maxInterval: new FormControl<string | null>(
              !isNil(itemTyped?.maxInterval) ? String(itemTyped.maxInterval) : null,
              intervalValidators,
            ),
            minInterval: new FormControl<string | null>(
              !isNil(itemTyped?.minInterval) ? String(itemTyped.minInterval) : null,
              intervalValidators,
            ),
            name: new FormControl<string | null>(
              itemTyped?.name ??
                this.translate.instant(
                  'pages.campaignForm.ctaDialog.ctaDetails.deliverables.chatbotMultiDeliverable.title',
                ),
              [Validators.required, Validators.maxLength(255)],
            ),
            sponsoredContentRelevant: new FormControl<boolean>(itemTyped?.sponsoredContentRelevant ?? true, {
              nonNullable: true,
            }),
            type: new FormControl<EDeliverableType.chatbotMulti>(EDeliverableType.chatbotMulti, { nonNullable: true }),
          },
          { validators: intervalRangeValidator() },
        ) as FormGroup<IChatbotMultiDeliverableForm>;

        if (isViewMode) {
          form.disable();
        }

        return form;
      },
    };
  }
}
